  import type { Match, SessionInfo } from '../../types';
  

export function formatMatchDate(date: Date) {
    return `${date.toLocaleDateString('nl-NL', { weekday: 'long' })} ${date.getDate()} ${date.toLocaleDateString('nl-NL', { month: 'short' })}`;
  }
  

export function getTeamData(report: Match, sessionInfo: SessionInfo, isAway: boolean) {
    const home = isAway ? {
      club: report.opponent_club?.name,
      name: report.opponent_team_name,
      location_name: report.opponent_club?.location_name || "",
      location_address: report.opponent_club?.location_address || "",
      location_zip_code: report.opponent_club?.location_zip_code || "",
      location_city: report.opponent_club?.location_city || "",
      logo: report.opponent_club?.logo_filename || "",
      score: report.score_opponent
    } : {
      club: sessionInfo.active_team.club?.name,
      name: sessionInfo.active_team.name,
      location_name: sessionInfo.active_team.club?.location_name || "",
      location_address: sessionInfo.active_team.club?.location_address || "",
      location_zip_code: sessionInfo.active_team.club?.location_zip_code || "",
      location_city: sessionInfo.active_team.club?.location_city || "",
      logo: sessionInfo.active_team.club?.logo_filename || "",
      score: report.score_team
    };
    const away = isAway ? {
      club: sessionInfo.active_team.club?.name,
      name: sessionInfo.active_team.name,
      location_name: sessionInfo.active_team.club?.location_name || "",
      location_address: sessionInfo.active_team.club?.location_address || "",
      location_zip_code: sessionInfo.active_team.club?.location_zip_code || "",
      location_city: sessionInfo.active_team.club?.location_city || "",
      logo: sessionInfo.active_team.club?.logo_filename || "",
      score: report.score_team
    } : {
      club: report.opponent_club?.name,
      name: report.opponent_team_name,
      location_name: report.opponent_club?.location_name || "",
      location_address: report.opponent_club?.location_address || "",
      location_zip_code: report.opponent_club?.location_zip_code || "",
      location_city: report.opponent_club?.location_city || "",
      logo: report.opponent_club?.logo_filename || "",
      score: report.score_opponent
    };

    return { home, away };
  }
  
  export function getGoogleMapsUrl(location_address: string, location_city: string, location_zip_code: string) {
    if (location_address && location_city) {
      return `https://www.google.com/maps/place/${encodeURIComponent(location_address)},${encodeURIComponent(location_city)},${encodeURIComponent(location_zip_code)}`;
    }
    return "";
  }

  export function getWeatherEmoji(condition) {
    const emojiMap = {
        'sunny': '☀️',
        'clear': '☀️',
        'partly cloudy': '⛅',
        'cloudy': '☁️',
        'overcast': '☁️',
        'rain': '🌧️',
        'light rain': '🌧️',
        'heavy rain': '⛈️',
        'snow': '🌨️',
        'light snow': '🌨️',
        'heavy snow': '🌨️',
        'mist': '🌫️',
        'fog': '🌫️'
    };
    
    const normalizedCondition = condition.toLowerCase().trim();
    return emojiMap[normalizedCondition] || '☀️';
  }
  
  export function roundToNearestHour(date) {
    const roundedDate = new Date(date);
    roundedDate.setMinutes(roundedDate.getMinutes() >= 30 ? 60 : 0);
    roundedDate.setSeconds(0);
    roundedDate.setMilliseconds(0);
    return roundedDate;
  }

  // Helper function to check if URL is from our server
  function isServerUrl(url: string): boolean {
    const API_URL = (import.meta as any).env?.VITE_API_URL || '/api';
    const baseUrl = API_URL.replace('/api', '');
    
    // Check if it's a relative URL starting with /uploads/ or /img/
    if (url.startsWith('/uploads/') || url.startsWith('/img/')) {
      return true;
    }
    
    // Check if it's an absolute URL pointing to our server
    if (baseUrl && url.startsWith(baseUrl)) {
      return true;
    }
    
    return false;
  }

  export function loadImagePromise(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      
      // If the image is from our server, we need to handle authentication
      if (isServerUrl(src)) {
        const token = localStorage.getItem('token');
        if (token) {
          // For server images, we need to fetch with auth headers and create a blob URL
          fetch(src, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          })
          .then(response => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.blob();
          })
          .then(blob => {
            const blobUrl = URL.createObjectURL(blob);
            img.src = blobUrl;
            
            // Clean up the blob URL after the image loads
            img.onload = () => {
              URL.revokeObjectURL(blobUrl);
              resolve(img);
            };
            img.onerror = () => {
              URL.revokeObjectURL(blobUrl);
              reject(new Error('Failed to load authenticated image'));
            };
          })
          .catch(error => {
            reject(error);
          });
        } else {
          // No token available, try loading without auth (might fail)
          img.src = src;
        }
      } else {
        // External image, load normally
        img.src = src;
      }
    });
  }

  export function getXforRect(frameWidth: number, columns: number, position: number, width: number) {
    return (frameWidth / columns) * position - (width / 2);
  }

  export function getTaskHeight(taskCount: number) {
    return (taskCount * 70) + 100;
  }

  export function wrapText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, maxWidth: number, lineHeight: number) {
    const words = text.split(' ');
    let line = '';
    let currentY = y;

    for (let n = 0; n < words.length; n++) {
      const testLine = line + words[n] + ' ';
      const metrics = ctx.measureText(testLine);
      const testWidth = metrics.width;

      if (testWidth > maxWidth && n > 0) {
        ctx.fillText(line, x, currentY);
        line = words[n] + ' ';
        currentY += lineHeight;
      } else {
        line = testLine;
      }
    }
    ctx.fillText(line, x, currentY);
    return currentY + lineHeight;
  }

  export function isShareAvailable() {
    // More permissive check to try sharing even in partial support cases
    return !!navigator.share;
  }